import { supabase } from './supabase';
import { getVisitorId } from './fingerprinting';
import { getUserIpAddress } from './api';
import React from 'react';
import { TFunction, i18n, t } from 'i18next';
import { User } from 'firebase/auth';
import { toast } from 'sonner';
import { debug } from './utils';
import { getDatabaseErrorTranslationKey } from './databaseErrors';
import type { Json } from './database.types';
import type { DatabaseScope } from './supabase';
import {
  DAILY_PUZZLE_GAMES,
  DailyPuzzleGame,
  formatDateString,
  parseDailyPuzzleIdentity
} from './dailyPuzzle';

export { formatDateString } from './dailyPuzzle';

// Check if a date is today
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
};

// Timer tick function for consistent timer behavior
export const createTimerTick = (
  setElapsedTime: React.Dispatch<React.SetStateAction<number>>,
  gameType: DailyPuzzleGame = 'sudoku'
) => {
  const storageKeys: Record<DailyPuzzleGame, string> = {
    sudoku: 'sudokuElapsedTime',
    '15puzzle': 'fifteen-puzzle-elapsed-time',
    '2048': 'game-2048-elapsed-time'
  };
  const localStorageKey = storageKeys[gameType];
  if (!localStorageKey) {
    throw new Error(`Unknown daily puzzle game: ${gameType}`);
  }

  return () => {
    setElapsedTime(prev => {
      const newTime = prev + 1;
      localStorage.setItem(localStorageKey, newTime.toString());
      return newTime;
    });
  };
};

// Format date as YYYY-MM-DD for URL
export const formatUrlDateString = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Function to extract puzzle number from a seed
export const extractPuzzleNumberFromSeed = (seed: number): number => {
  return seed % 100; // Extract the last two digits as puzzle number
};

// Format display date for game UI
export const formatDisplayDate = (date: Date, t: TFunction, i18n: i18n, gameType: DailyPuzzleGame, completed: boolean, isPreviewMode: boolean = false): string => {
  const translationKeys: Record<DailyPuzzleGame, {
    today: string;
    todayCompleted: string;
    date: string;
    dateCompleted: string;
  }> = {
    sudoku: {
      today: 'todaysSudoku',
      todayCompleted: 'todaysSudokuCompleted',
      date: 'dateSudoku',
      dateCompleted: 'dateSudokuCompleted'
    },
    '15puzzle': {
      today: 'todays15Puzzle',
      todayCompleted: 'todays15PuzzleCompleted',
      date: 'date15Puzzle',
      dateCompleted: 'date15PuzzleCompleted'
    },
    '2048': {
      today: 'todays2048',
      todayCompleted: 'todays2048Completed',
      date: 'date2048',
      dateCompleted: 'date2048Completed'
    }
  };
  const keys = translationKeys[gameType];
  if (!keys) {
    throw new Error(`Unknown daily puzzle game: ${gameType}`);
  }

  if (isToday(date) || isPreviewMode) {
    return t(completed ? keys.todayCompleted : keys.today);
  }

  const locale = i18n.language || 'en';
  return t(completed ? keys.dateCompleted : keys.date, {
    date: new Intl.DateTimeFormat(locale, {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date)
  });
};

// Format time display for games
export const formatTimeDisplay = (elapsedTime: number, includeHours: boolean = true): string => {
  if (includeHours && elapsedTime >= 3600) {
    return `${Math.floor(elapsedTime / 3600).toString()}:${Math.floor((elapsedTime % 3600) / 60).toString().padStart(2, '0')}:${(elapsedTime % 60).toString().padStart(2, '0')}`;
  } else {
    return `${Math.floor(elapsedTime / 60).toString().padStart(2, '0')}:${(elapsedTime % 60).toString().padStart(2, '0')}`;
  }
};

// Format completion time for display
export const formatCompletionTime = (elapsedTime: number): string => {
  if (elapsedTime >= 3600) {
    return `${Math.floor(elapsedTime / 3600)}h ${Math.floor((elapsedTime % 3600) / 60)}m ${elapsedTime % 60}s`;
  } else {
    return `${Math.floor(elapsedTime / 60)}m ${elapsedTime % 60}s`;
  }
};

// Check if another game is completed for the same date
const completionStorage: Record<DailyPuzzleGame, { completed: string; identity: string }> = {
  sudoku: { completed: 'sudokuCompleted', identity: 'sudokuPuzzleIdentity' },
  '15puzzle': { completed: 'fifteen-puzzle-completed', identity: 'fifteen-puzzle-identity' },
  '2048': { completed: 'game-2048-completed', identity: 'game-2048-identity' }
};

const locallyCompletedGames = (currentPuzzleDate: Date): Set<DailyPuzzleGame> => {
  const completed = new Set<DailyPuzzleGame>();
  const expectedDate = formatDateString(currentPuzzleDate);

  DAILY_PUZZLE_GAMES.forEach(gameType => {
    const keys = completionStorage[gameType];
    if (localStorage.getItem(keys.completed) !== 'true') {
      return;
    }

    const serializedIdentity = localStorage.getItem(keys.identity);
    if (!serializedIdentity) {
      return;
    }

    const identity = parseDailyPuzzleIdentity(serializedIdentity);
    if (identity.gameType === gameType && identity.date === expectedDate) {
      completed.add(gameType);
    }
  });

  return completed;
};

export const getIncompleteDailyGames = async (
  currentPuzzleDate: Date,
  completedGameType: DailyPuzzleGame,
  _currentUser: User | null,
  database = supabase
): Promise<DailyPuzzleGame[]> => {
  const completed = locallyCompletedGames(currentPuzzleDate);
  completed.add(completedGameType);
  const missing = DAILY_PUZZLE_GAMES.filter(gameType => !completed.has(gameType));
  if (missing.length === 0) {
    return [];
  }

  const dateString = formatDateString(currentPuzzleDate);
  const { data, error } = await database
    .from('games')
    .select('game_type')
    .eq('puzzle_date', dateString)
    .in('game_type', missing);

  if (error) {
    throw error;
  }
  if (!Array.isArray(data)) {
    throw new TypeError('Invalid games completion response');
  }

  data.forEach(row => {
    if (
      typeof row !== 'object' ||
      row === null ||
      !('game_type' in row) ||
      !missing.includes(row.game_type as DailyPuzzleGame)
    ) {
      throw new TypeError('Invalid games completion response');
    }
    completed.add(row.game_type as DailyPuzzleGame);
  });

  return DAILY_PUZZLE_GAMES.filter(gameType => !completed.has(gameType));
};

export const checkGameCompletion = async (
  currentPuzzleDate: Date,
  completedGameType: DailyPuzzleGame,
  currentUser: User | null
): Promise<boolean> => {
  const incompleteGames = await getIncompleteDailyGames(
    currentPuzzleDate,
    completedGameType,
    currentUser
  );
  return incompleteGames.length === 0;
};

// Save game data to database
export const saveGameToDatabase = async (
  gameType: DailyPuzzleGame,
  currentPuzzleDate: Date,
//  puzzleNumber: number,
  difficultyValue: number,
  finalTime: number,
  gameStats: Json,
  _currentUser: User | null,
  options?: { database?: DatabaseScope['client']; submissionId?: string; quiet?: boolean }
) => {
  try {
    // Get today's date in YYYY-MM-DD format
    const dateString = formatDateString(currentPuzzleDate);

    const visitorId = await getVisitorId();
    const ipAddress = await getUserIpAddress();
    const gameData = {
      ...(options?.submissionId ? { submission_id: options.submissionId } : {}),
      puzzle_date: dateString,
      completion_time_seconds: finalTime,
      game_stats: gameStats,
      game_type: gameType,
      difficulty: difficultyValue,
      visitor_id: visitorId,
      ip_address: ipAddress,
      user_agent: navigator.userAgent
    };

    // Insert the game record into the database
    const database = options?.database ?? supabase;
    const { error } = await database
      .from('games')
      .insert(gameData);

    if (error) {
      if (error.code === '23505' && options?.submissionId) {
        const { data: existing, error: lookupError } = await database.from('games')
          .select('id').eq('submission_id', options.submissionId).eq('game_type', gameType);
        if (lookupError) {
          throw lookupError;
        }
        if (!Array.isArray(existing) || existing.some(row => row === null || typeof row !== 'object' || typeof row.id !== 'string')) {
          throw new TypeError('Invalid game submission lookup response');
        }
        if (existing.length === 1) {
          return { success: true, error: null };
        }
      }
      if (!options?.quiet) {
        toast.error(t(getDatabaseErrorTranslationKey(error, 'errorSavingGame')));
      }
      return { success: false, error };
    }

    if (!options?.quiet) {
      toast.success(t('gameSaved'));
    }
    return { success: true, error: null };
  } catch (err) {
    if (!options?.quiet) {
      toast.error(t(getDatabaseErrorTranslationKey(err, 'errorSavingGame')));
    }
    return { success: false, error: err };
  }
};

// Check if this is a personal best time
export const checkPersonalBest = async (
  gameType: DailyPuzzleGame,
  finalTime: number,
  isPerfectSolve: boolean,
  difficultyValue: number,
  _currentUser: User | null,
  database = supabase
): Promise<boolean> => {
  if (!isPerfectSolve) return false;

  const { data: previousGames, error } = await database
    .from('games')
    .select('completion_time_seconds, game_stats')
    .eq('game_type', gameType)
    .eq('difficulty', difficultyValue)
    .eq('game_stats->perfect_solve', true)
    .order('completion_time_seconds', { ascending: true })
    .limit(1);

  if (error) {
    throw error;
  }

  const previousBest = previousGames[0];
  debug('previousBest', previousBest);
  return Boolean(previousBest && finalTime < previousBest.completion_time_seconds);
};
